// OrgMenu — the organisation switcher behind the Home title. Orgs are
// context, not navigation: it opens where you tapped, as a small menu
// anchored under the title, and closes on a choice or a tap outside.

import { useEffect, useId, useRef } from "react";

import { mobileStrings } from "~/components/mobile/strings";
import { getInitials } from "~/lib/initials";
import type { RoomSummary } from "~/lib/matrix";

/** An org's mark: its initial in a quiet tile. `null` (every org) shows the
 *  Nurion symbol. */
export function OrgMark({ name, size }: { name: string | null; size: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex shrink-0 items-center justify-center rounded-md bg-foreground/[0.08] font-medium text-foreground/90"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
    >
      {name === null ? (
        <NurionSymbol size={Math.round(size * 0.6)} />
      ) : (
        getInitials(name).slice(0, 1)
      )}
    </span>
  );
}

function NurionSymbol({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 213 217" fill="currentColor">
      <path d="M74.3149 0.93457C118.344 0.93457 147.844 28.648 147.846 70.4418V96.013L106.717 108.977V71.1213C106.717 49.4414 93.9833 34.9103 74.7619 34.9103C55.5418 34.9115 42.8068 49.4422 42.8068 71.1213V129.095L0.78418 142.327V70.4418C0.785443 28.6487 30.2867 0.935541 74.3149 0.93457Z" />
      <path d="M139.061 213.934C95.0315 213.934 65.5315 186.221 65.5303 144.427L65.5303 121.878L106.659 108.913V143.747C106.659 165.427 119.393 179.958 138.614 179.958C157.834 179.957 170.569 165.427 170.569 143.747L170.569 88.7961L212.592 75.5635V144.427C212.59 186.22 183.089 213.933 139.061 213.934Z" />
    </svg>
  );
}

/** Rendered inside the title's `relative` wrapper so it sits right under it. */
export function OrgMenu({
  spaces,
  activeSpaceId,
  unreadBySpace,
  onSelect,
  onClose,
}: {
  spaces: RoomSummary[];
  activeSpaceId: string | null;
  unreadBySpace: Map<string, number>;
  onSelect: (spaceId: string | null) => void;
  onClose: () => void;
}) {
  const labelId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    listRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"] button')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const choose = (spaceId: string | null) => {
    onSelect(spaceId);
    onClose();
  };

  return (
    <>
      <button
        type="button"
        aria-label={mobileStrings.home.closeOrgMenu}
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div
        role="dialog"
        aria-labelledby={labelId}
        className="absolute left-1 top-full z-50 mt-1 w-72 overflow-hidden rounded-xl bg-background py-1 shadow-[0_8px_32px_rgb(0_0_0/0.35)] ring-1 ring-foreground/[0.06]"
      >
        <span id={labelId} className="sr-only">
          {mobileStrings.home.orgSheetTitle}
        </span>
        <ul
          ref={listRef}
          role="listbox"
          aria-labelledby={labelId}
          className="max-h-[60vh] overflow-y-auto"
        >
          <OrgRow
            label={mobileStrings.home.allOrgs}
            markName={null}
            selected={activeSpaceId === null}
            unread={0}
            onSelect={() => choose(null)}
          />
          {spaces.map((space) => (
            <OrgRow
              key={space.id}
              label={space.name}
              markName={space.name}
              selected={activeSpaceId === space.id}
              unread={unreadBySpace.get(space.id) ?? 0}
              onSelect={() => choose(space.id)}
            />
          ))}
        </ul>
      </div>
    </>
  );
}

function OrgRow({
  label,
  markName,
  selected,
  unread,
  onSelect,
}: {
  label: string;
  markName: string | null;
  selected: boolean;
  unread: number;
  onSelect: () => void;
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        onClick={onSelect}
        className="flex h-11 w-full items-center gap-3 px-3 text-left outline-none transition-colors hover:bg-foreground/[0.04] focus-visible:bg-foreground/[0.06]"
      >
        <OrgMark name={markName} size={24} />
        {/* Selection changes ink, not shape (DESIGN_CHEATSHEET "Buttons"), so
            the count keeps one trailing column in every row. */}
        <span
          className={`flex-1 truncate text-sm font-light ${
            selected ? "text-foreground/90" : "text-muted-foreground"
          }`}
        >
          {label}
        </span>
        {unread > 0 && (
          <span
            aria-label={mobileStrings.home.orgUnread(unread)}
            className="min-w-5 rounded-full bg-foreground px-1.5 text-center text-xs font-medium leading-5 text-background tabular-nums"
          >
            {unread}
          </span>
        )}
      </button>
    </li>
  );
}
